class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      // Derive the filter params from history state when present, but fall back
      // to the actual URL (not searchParamsInitial) whenever state is missing or
      // was written by another engine — e.g. the filter drawer pushes { path }.
      // Without this fallback, event.state.searchParams is `undefined` and
      // renderPage builds a malformed `?section_id=…&undefined` request that the
      // server answers with the UNFILTERED catalog, repainting the full catalog
      // while the URL still shows a filtered query on Back/Forward.
      const searchParams =
        event.state && event.state.searchParams !== undefined
          ? event.state.searchParams
          : window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    // Abort any in-flight filter fetch so a slower earlier request can't resolve
    // late and overwrite this newer one (stale-swap guard, fetch phase).
    if (FacetFiltersForm.abortController) FacetFiltersForm.abortController.abort();
    FacetFiltersForm.abortController = new AbortController();

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static parseHTML(html) {
    if (typeof html === 'string') return new DOMParser().parseFromString(html, 'text/html');
    return html;
  }

  static renderSectionFromFetch(url, event) {
    const controller = FacetFiltersForm.abortController;
    fetch(url, controller ? { signal: controller.signal } : {})
      .then((response) => response.text())
      .then((responseText) => {
        const parsed = FacetFiltersForm.parseHTML(responseText);
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html: parsed, url }];
        FacetFiltersForm.renderFilters(parsed, event);
        FacetFiltersForm.renderProductGridContainer(parsed);
        FacetFiltersForm.renderProductCount(parsed);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(document.getElementById('ProductGridContainer'));
      })
      .catch((error) => {
        // A newer filter request aborted this one — it now owns the loading state,
        // so bail without logging or clearing spinners (which would flash the UI).
        if (error.name === 'AbortError') return;
        // Error handling: Log error and reset UI loading states to prevent stuck spinners
        console.error('Error fetching facet filters:', error);
        const loadingSpinners = document.querySelectorAll(
          '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
        );
        loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
        const gridContainer = document.getElementById('ProductGridContainer');
        if (gridContainer) {
          const collection = gridContainer.querySelector('.collection');
          if (collection) collection.classList.remove('loading');
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    const parsed = FacetFiltersForm.parseHTML(html);
    FacetFiltersForm.renderFilters(parsed, event);
    // Instant swap — this category is already cached (HTML + images warm), so skip
    // the dim/decode loading state and render it immediately.
    FacetFiltersForm.renderProductGridContainer(parsed, true);
    FacetFiltersForm.renderProductCount(parsed);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(document.getElementById('ProductGridContainer'));
  }

  // Seed the cache with the currently displayed grid (notably the initial,
  // server-rendered category, which is otherwise never cached) under its filter
  // key, so the first return to it is an instant cache hit instead of a fresh
  // fetch + loading state. No-ops when that key is already cached — AJAX-swapped
  // grids cache themselves on fetch. `searchParams` must be built the same way a
  // filter click builds it, so the key matches the later lookup byte-for-byte.
  static cacheCurrentSection(searchParams) {
    const grid = document.getElementById('product-grid');
    if (!grid || !grid.dataset.id) return;
    const url = `${window.location.pathname}?section_id=${grid.dataset.id}&${searchParams}`;
    if (FacetFiltersForm.filterData.some((entry) => entry.url === url)) return;
    const snapshot = FacetFiltersForm.parseHTML(document.documentElement.outerHTML);
    FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html: snapshot, url }];
  }

  static renderProductGridContainer(parsed, instant = false) {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    const parsedContainer = parsed.getElementById('ProductGridContainer');
    if (!parsedContainer) return;

    parsedContainer.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    FacetFiltersForm.swapGridWhenReady(container, parsedContainer.innerHTML, instant);
  }

  // Double-buffer the grid swap: keep the current grid visible (dimmed) while the
  // incoming grid loads HIDDEN in the background; once its above-the-fold images
  // have decoded, reveal the whole new grid at once. No blank gap, no per-image
  // popcorn. Matches: click -> grid fades out -> new grid loads in background ->
  // whole new grid appears together.
  static swapGridWhenReady(container, newHTML, instant = false) {
    let stage = null;
    // Bump a generation token so a slow background load that finishes AFTER a newer
    // filter click can't overwrite the newer grid (stale-swap guard).
    const generation = (FacetFiltersForm.swapGeneration = (FacetFiltersForm.swapGeneration || 0) + 1);

    const apply = () => {
      // Remove the swap state BEFORE inserting so the incoming #product-grid is
      // created at full opacity and appears at once with NO fade-in.
      container.classList.remove('is-swapping');
      container.innerHTML = newHTML;
      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger(container);
      }
      FacetFiltersForm.upgradeAboveFoldImages();
    };

    // Cached render: the section HTML + its images are already warm (this category
    // was viewed earlier this session), so swap instantly — the dim + background
    // decode would just be a needless loading flash.
    if (instant) {
      apply();
      return;
    }

    const finish = () => {
      if (stage && stage.parentNode) stage.remove();
      // A newer swap superseded this one (user clicked again during the background
      // load) — drop this stale result instead of overwriting the newer grid.
      if (generation !== FacetFiltersForm.swapGeneration) return;
      apply();
    };

    try {
      container.classList.add('is-swapping'); // dim the outgoing grid (stays on screen — no blank)

      // Warm the cache by preloading ONLY the above-the-fold images as bare <img>
      // clones — NOT the card markup. Staging full cards would connect their custom
      // elements (modal-dialog, sliders, …) in the wrong context and throw. Bare
      // imgs carry the same srcset+sizes, so they fetch the exact candidate the real
      // grid will use; the final swap then paints those images together from cache.
      const doc = new DOMParser().parseFromString(newHTML, 'text/html');
      // Above-the-fold ≈ 2 rows, but rows-per-screen depend on the column count,
      // which differs by breakpoint (e.g. 4-up desktop vs 2-up mobile). A fixed 8
      // would over-wait on mobile (8 imgs ≈ 4 rows there) and delay the reveal, so
      // derive the per-row count from the live grid + current breakpoint.
      let perRow = 4;
      const liveGrid = document.getElementById('product-grid');
      if (liveGrid) {
        const isDesktop = window.matchMedia('(min-width: 990px)').matches;
        const match = liveGrid.className.match(
          isDesktop ? /grid--(\d+)-col-desktop/ : /grid--(\d+)-col-tablet-down/
        );
        if (match) perRow = parseInt(match[1], 10) || perRow;
      }
      const aboveFold = Math.max(2, perRow * 2);
      const sources = Array.from(
        doc.querySelectorAll('.card-image-slider__list .slider__slide:first-child img')
      ).slice(0, aboveFold);
      if (!sources.length) {
        finish();
        return;
      }

      stage = document.createElement('div');
      stage.setAttribute('aria-hidden', 'true');
      stage.style.cssText =
        'position:fixed;left:0;top:0;width:0;height:0;overflow:hidden;opacity:0;pointer-events:none;z-index:-1;';
      const clones = sources.map((src) => {
        const img = document.createElement('img');
        const srcset = src.getAttribute('srcset');
        const sizes = src.getAttribute('sizes');
        if (srcset) img.setAttribute('srcset', srcset);
        if (sizes) img.setAttribute('sizes', sizes);
        img.setAttribute('src', src.getAttribute('src') || '');
        img.loading = 'eager';
        stage.appendChild(img);
        return img;
      });
      document.body.appendChild(stage);

      if (typeof clones[0].decode !== 'function') {
        finish();
        return;
      }
      const decoded = Promise.all(clones.map((img) => img.decode().catch(() => {})));
      // Generous safety cap: the old grid stays visible the whole time (no blank to
      // race against), so this only bounds a pathologically slow/broken image.
      const cap = new Promise((resolve) => setTimeout(resolve, 1500));
      Promise.race([decoded, cap]).then(finish);
    } catch (e) {
      finish();
    }
  }

  // Eager-load the first two rows up front instead of lazily, so the collection's
  // initial above-the-fold grid doesn't pop in image-by-image. First two rows =
  // 8 cards on desktop (4-up), 4 on mobile (2-up). card-product renders only the
  // first 2 eager, so this upgrades the rest of the first two rows. Setting eager
  // on the real card <img> is what forces the load (a preload wouldn't paint it).
  static upgradeAboveFoldImages() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    const eagerCount = window.matchMedia('(min-width: 990px)').matches ? 8 : 4;
    const items = Array.from(container.querySelectorAll('#product-grid > li')).slice(0, eagerCount);
    items.forEach((li) => {
      const img =
        li.querySelector('.card-image-slider__list .slider__slide:first-child img') || li.querySelector('img');
      if (img && img.getAttribute('loading') !== 'eager') {
        img.setAttribute('loading', 'eager');
        img.loading = 'eager';
      }
    });
  }

  static renderProductCount(parsed) {
    const parsedCount = parsed.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (container && parsedCount) {
      container.innerHTML = parsedCount.innerHTML;
      container.classList.remove('loading');
    }
    if (containerDesktop && parsedCount) {
      containerDesktop.innerHTML = parsedCount.innerHTML;
      containerDesktop.classList.remove('loading');
    }

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(parsed, event) {
    const facetDetailsElementsFromFetch = parsed.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsed);
    FacetFiltersForm.renderAdditionalElements(parsed);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const isMobileCheckbox = event.srcElement.className == 'mobile-facets__checkbox';
    const isMobile = isMobileCheckbox || event.target.closest('form').id === 'FacetFiltersFormMobile';

    const relevantForms = [];
    sortFilterForms.forEach((form) => {
      if (isMobile) {
        if (form.id === 'FacetFiltersFormMobile') relevantForms.push(form);
      } else if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
        relevantForms.push(form);
      }
    });

    // Preserve URL params not owned by any of the active facet forms (e.g. quick-filter pill params).
    const merged = new URLSearchParams(window.location.search);
    merged.delete('page');
    const ownedKeys = new Set();
    relevantForms.forEach((form) => {
      form.querySelectorAll('[name]').forEach((input) => ownedKeys.add(input.name));
    });
    ownedKeys.forEach((key) => merged.delete(key));
    relevantForms.forEach((form) => {
      new FormData(form).forEach((value, key) => merged.append(key, value));
    });

    this.onSubmitForm(merged.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
// Eager-load the first two rows of the initial (server-rendered) collection grid so
// it doesn't pop in image-by-image above the fold. Guarded to the collection page.
if (document.querySelector('facet-filters-form')) FacetFiltersForm.upgradeAboveFoldImages();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);